// @story HIR-HR34
// A candidate's whole route on one card (server: services/roundViews
// .service.js): every round in process order, with its marks, who decided
// what, the reason and the mark at the time. It opens on the round being
// looked at; the others sit below as summaries that open in turn. For a
// panellist the server sends only their round and the ones before it, and no
// name; this screen shows what it is given.
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { StatusPill } from "../../ds";
import { Section, Note } from "../../components/page";

const muted = { color: "var(--text-muted)", fontSize: "var(--fs-caption)" };
const soft = { background: "var(--surface-2)", borderRadius: "var(--radius-sm)", padding: "6px 10px", fontSize: "var(--fs-body-sm)" };
const when = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "");

function Round({ r }) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {r.marksWithheld && <span style={muted}>Marks show once you have submitted yours.</span>}
      {r.panellists?.map((p, i) => (
        <div key={i} style={soft}>
          {p.name}: <strong className="num">{p.score}</strong>{p.enteredBy && <span style={muted}> (typed by {p.enteredBy})</span>}
          {p.notes && <div style={muted}>{p.notes}</div>}
        </div>
      ))}
      {r.sections?.length > 0 && (
        <div style={muted}>{r.sections.map((s) => `${s.label}: ${s.earned} / ${s.total}`).join(" · ")}</div>
      )}
      {r.decisions.map((d, i) => (
        <div key={i} style={soft}>
          <strong>{d.kind === "HR" ? "HR" : d.kind === "exit filter" ? "Exit filter" : d.kind === "panel" ? "Panel" : "Recorded"}: {d.decision}</strong>
          {d.reason && <> — {d.reason}</>}
          <span style={muted}>
            {d.by ? ` · ${d.by}` : ""}{d.at ? ` · ${when(d.at)}` : ""}
            {typeof d.markThen === "number" ? ` · mark then ${d.markThen}` : ""}{typeof d.totalThen === "number" ? `, total then ${d.totalThen}` : ""}
          </span>
        </div>
      ))}
      {!r.decisions.length && !r.panellists?.length && !r.marksWithheld && <span style={muted}>Nothing recorded yet.</span>}
    </div>
  );
}

function Title({ r }) {
  return (
    <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <span style={{ fontWeight: 500, color: "var(--text-1)" }}>{r.label}</span>
      {typeof r.mark === "number" && <span className="num" style={{ fontWeight: 600 }}>{r.mark}%</span>}
      {r.noMarks && <StatusPill tone="neutral">{r.recorded || "No marks"}</StatusPill>}
      {r.referenceOnly && <StatusPill tone="neutral">Reference only</StatusPill>}
      <span style={muted}>{String(r.status || "").replace(/_/g, " ")}</span>
    </span>
  );
}

export default function RouteCard({ resumeId, stageId }) {
  const [st, setSt] = useState({ phase: "loading" });
  useEffect(() => {
    let live = true;
    api.get(`/round-views/candidates/${resumeId}/route`, { params: stageId ? { stageId } : {} })
      .then((r) => { if (live) setSt({ phase: "ready", ...r.data.data }); })
      .catch((e) => { if (live) setSt({ phase: "error", message: e?.response?.data?.message || "The route could not be read just now." }); });
    return () => { live = false; };
  }, [resumeId, stageId]);

  if (st.phase === "loading") return null;
  if (st.phase === "error") return <Note tone="warning">{st.message}</Note>;
  const focus = st.rounds.find((r) => r.stageId === st.focus);
  const others = st.rounds.filter((r) => r.stageId !== st.focus).reverse();
  return (
    <Section title="Route" subtitle={`${st.name || st.reference} · every round in process order`}>
      <div style={{ display: "grid", gap: 10 }} data-testid="route-card">
        {st.status && <div style={muted}>Total {st.total ?? "—"} / 100{st.grade ? ` · grade ${st.grade}` : ""} · {st.status}</div>}
        {focus && (
          <div style={{ display: "grid", gap: 6 }}>
            <Title r={focus} />
            <Round r={focus} />
          </div>
        )}
        {others.map((r) => (
          <details key={r.stageId}>
            <summary style={{ cursor: "pointer" }}><Title r={r} /></summary>
            <div style={{ marginTop: 6 }}><Round r={r} /></div>
          </details>
        ))}
      </div>
    </Section>
  );
}
